import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { PublicLayout } from "@/components/layout/PublicLayout";
import { Card } from "@/components/ui/Card";
import { Logo } from "@/components/ui/Logo";
import { fetchPublicProfile } from "@/lib/api/publicProfile";

/**
 * Public participant profile served at /p/:participantId.
 *
 * Linked from the auto-generated Telegram intro message that one matched
 * participant sends to the other. Shows only safe-to-share fields — no
 * email, no contact_for_matches. The link is unauthenticated; security
 * comes from the unguessability of the participant UUID, same model as
 * meetup invitation links.
 */
export function ParticipantProfilePage() {
  const { participantId } = useParams<{ participantId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-participant", participantId],
    queryFn: () => fetchPublicProfile(participantId ?? ""),
    enabled: Boolean(participantId),
  });

  return (
    <PublicLayout centered={false}>
      <div className="mx-auto w-full max-w-[560px]">
        <Link to="/" className="mb-6 inline-block no-underline">
          <Logo size="sm" />
        </Link>

        {isLoading ? (
          <Card padding="lg">
            <p className="text-[14px] text-[#94a3b8]">Loading profile…</p>
          </Card>
        ) : error || !data ? (
          <Card padding="lg">
            <h1 className="text-[20px] font-bold text-[#1a1a1a]">Profile not found</h1>
            <p className="mt-2 text-[14px] text-[#475569]">
              This link is no longer valid, or the participant left the event.
            </p>
            <Link
              to="/"
              className="mt-4 inline-block text-[14px] font-semibold text-[#2563eb] no-underline"
            >
              Back to Finally →
            </Link>
          </Card>
        ) : (
          <ProfileCard profile={data} />
        )}
      </div>
    </PublicLayout>
  );
}

function ProfileCard({
  profile,
}: {
  profile: NonNullable<Awaited<ReturnType<typeof fetchPublicProfile>>>;
}) {
  const roleLine = [profile.role, profile.company].filter(Boolean).join(" · ");

  return (
    <Card padding="lg">
      <p className="text-[11px] font-bold uppercase tracking-[1.2px] text-[#2563eb]">
        Matched on Finally
      </p>
      <h1 className="mt-2 break-words text-[24px] font-bold leading-tight tracking-tight text-[#1a1a1a]">
        {profile.name}
      </h1>
      {roleLine ? (
        <p className="mt-1 break-words text-[14px] text-[#6c757d]">{roleLine}</p>
      ) : null}
      <p className="mt-3 text-[13px] text-[#94a3b8]">
        From the “{profile.event_title}” event
      </p>

      {profile.headline ? (
        <p className="mt-5 break-words text-[15px] leading-relaxed text-[#1a1a1a]">
          {profile.headline}
        </p>
      ) : null}

      {profile.looking_for ? (
        <Section title="Looking for" body={profile.looking_for} />
      ) : null}

      {profile.can_help_with ? (
        <Section title="Can help with" body={profile.can_help_with} />
      ) : null}

      <div className="mt-8 rounded-[10px] bg-[#f8fafc] p-4 text-[13px] text-[#475569]">
        Want a profile like this?{" "}
        <a
          href="https://meetup.finally.plus"
          className="font-semibold text-[#2563eb] no-underline"
        >
          Run your next meetup with Finally →
        </a>
      </div>
    </Card>
  );
}

function Section({ title, body }: { title: string; body: string }) {
  return (
    <div className="mt-5">
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#2563eb]">
        {title}
      </p>
      <p className="mt-1 break-words text-[14px] leading-relaxed text-[#475569]">
        {body}
      </p>
    </div>
  );
}
